import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, ArrowRight, Check, Sparkles } from "lucide-react";

const ReferralCodeStep = ({ formData, setFormData, onNext, onPrev }) => {
  const [selected, setSelected] = useState(
    formData.hasReferralCode === true
      ? "yes"
      : formData.hasReferralCode === false
      ? "no"
      : null
  );
  const [referralInput, setReferralInput] = useState(
    formData.referralCode || ""
  );
  const [touched, setTouched] = useState(false);

  const canContinue =
    selected === "no" ||
    (selected === "yes" && referralInput.trim().length > 0);

  const handleContinue = () => {
    if (!canContinue) return;
    setFormData((prev) => ({
      ...prev,
      hasReferralCode: selected === "yes",
      referralCode: selected === "yes" ? referralInput.trim() : "",
    }));
    onNext();
  };

  const handleSelect = (value) => {
    setSelected(value);
    setTouched(true);
    if (value === "no") setReferralInput("");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="py-8 mx-auto w-full max-w-2xl"
    >
      {/* Header with subtle icon */}
      <div className="mb-10 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200/60 shadow-sm"
        >
          <Gift className="w-8 h-8 text-amber-600" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-3 text-2xl font-bold text-gray-900 sm:text-3xl"
        >
          Do you have a referral code?
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mx-auto max-w-md text-base text-gray-500"
        >
          If a friend or partner shared a code with you, enter it below for a
          smoother onboarding.
        </motion.p>
      </div>

      {/* Yes / No cards */}
      <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2">
        {[
          {
            id: "yes",
            label: "Yes, I have one",
            sublabel: "I'll enter my code",
            icon: Sparkles,
            gradient: "from-emerald-500 to-teal-600",
            bg: "bg-emerald-50",
            border: "border-emerald-200",
            selectedBorder: "border-emerald-500",
          },
          {
            id: "no",
            label: "No, I don't",
            sublabel: "Continue without a code",
            icon: Check,
            gradient: "from-slate-500 to-slate-600",
            bg: "bg-slate-50",
            border: "border-slate-200",
            selectedBorder: "border-slate-500",
          },
        ].map((option) => {
          const Icon = option.icon;
          const isSelected = selected === option.id;
          return (
            <motion.button
              key={option.id}
              type="button"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + (option.id === "yes" ? 0 : 0.05) }}
              onClick={() => handleSelect(option.id)}
              className={`relative w-full text-left rounded-xl border-2 p-5 transition-all duration-200 ${option.bg} ${option.border} ${
                isSelected ? `${option.selectedBorder} shadow-lg ring-2 ring-offset-2 ring-offset-white ring-amber-400/30` : "hover:border-gray-300 hover:shadow-md"
              }`}
            >
              <div className="absolute top-4 right-4">
                <motion.div
                  initial={false}
                  animate={{
                    scale: isSelected ? 1 : 0,
                    opacity: isSelected ? 1 : 0,
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className={`w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-r ${option.gradient} text-white`}
                >
                  <Check className="w-4 h-4" />
                </motion.div>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${option.gradient} text-white shadow-sm`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.sublabel}</div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Conditional referral code input */}
      <AnimatePresence mode="wait">
        {selected === "yes" && (
          <motion.div
            key="referral-input"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <motion.div
              initial={{ y: -8 }}
              animate={{ y: 0 }}
              className="mb-8"
            >
              <label
                htmlFor="referral-code"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Referral code
              </label>
              <input
                id="referral-code"
                type="text"
                value={referralInput}
                onChange={(e) => setReferralInput(e.target.value)}
                placeholder="e.g. FRIEND2024 or PARTNER-XY"
                className="w-full px-4 py-3 text-gray-900 placeholder-gray-400 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all duration-200"
                autoFocus
                autoComplete="off"
              />
              {touched && selected === "yes" && referralInput.trim().length === 0 && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2 text-sm text-amber-600"
                >
                  Enter your referral code to continue.
                </motion.p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Continue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
      >
        <motion.button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue}
          whileHover={canContinue ? { scale: 1.02 } : {}}
          whileTap={canContinue ? { scale: 0.98 } : {}}
          className={`inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-medium rounded-xl transition-all duration-200 ${
            canContinue
              ? "text-white bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl"
              : "text-gray-400 bg-gray-100 cursor-not-allowed"
          }`}
        >
          <span>Continue</span>
          <ArrowRight className="w-5 h-5" />
        </motion.button>
        {!canContinue && selected === "yes" && (
          <span className="text-sm text-gray-500">
            Add your referral code above to continue
          </span>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ReferralCodeStep;
